import { useRef, useState } from 'react';

import ChevronLeft from '@mui/icons-material/ChevronLeft';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import { alpha } from '@mui/material/styles';

import { useIsMobile } from '@@common';

import { BoxResizeHandle } from '../../common/components/BoxResizeHandle';
import PromptChat from '../../components/Chat/PromptChat';
import { ChatMessage, PromptChatAdapter } from '../../components/Chat/PromptChatAdapter';
import { appShape } from '../../themes/shapeTokens';

const LOCAL_STORAGE_KEY = 'templateChatPanel.width';
const DEFAULT_WIDTH = 360;
const MIN_WIDTH = 280;
const MAX_WIDTH = 720;

type TemplateChatPanelProps = {
	adapter: PromptChatAdapter;
	onComplete?: () => void | Promise<void>;
	onAssistantMessageReceived?: (message: ChatMessage) => void | Promise<void>;
};

export default function TemplateChatPanel({ adapter, onComplete, onAssistantMessageReceived }: TemplateChatPanelProps) {
	const [panelWidth, setPanelWidth] = useState<number>(DEFAULT_WIDTH);
	const [isCollapsed, setIsCollapsed] = useState(false);
	const panelRef = useRef<HTMLDivElement>(null);
	const isMobile = useIsMobile();

	return (
		<Paper
			ref={panelRef}
			sx={(theme) => ({
				position: 'relative',
				width: isCollapsed ? 48 : isMobile ? '100%' : `${panelWidth}px`,
				flexBasis: isCollapsed ? 48 : isMobile ? '100%' : panelWidth,
				height: isCollapsed ? { xs: 48, sm: 'auto' } : { xs: 360, sm: 'auto' },
				alignSelf: isCollapsed && isMobile ? 'flex-end' : undefined,
				display: 'flex',
				flexDirection: 'column',
				borderLeft: 0,
				borderTop: 0,
				borderColor: 'divider',
				minHeight: 0,
				flexShrink: 0,
				borderRadius: isMobile ? 0 : appShape.surfaceRadius,
				boxShadow: isMobile ? 'none' : `0 8px 28px ${alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.2 : 0.08)}`,
				border: isMobile ? undefined : `1px solid ${alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.18 : 0.08)}`,
				overflow: 'hidden',
				mr: !isCollapsed && !isMobile ? `${theme.layout.edgeGap}px` : 0,
				mb: !isCollapsed && !isMobile ? `${theme.layout.edgeGap}px` : 0,
			})}
		>
		{!isCollapsed && !isMobile && (
				<BoxResizeHandle
					position="left"
					width={panelWidth}
					setWidth={setPanelWidth}
					panelRef={panelRef}
					storageKey={LOCAL_STORAGE_KEY}
					defaultWidth={DEFAULT_WIDTH}
					minWidth={MIN_WIDTH}
					maxWidth={MAX_WIDTH}
					direction="right"
				/>
			)}

		{isCollapsed && (
			<Tooltip title="Expand chat" placement="left">
				<IconButton aria-label="Expand chat" onClick={() => setIsCollapsed(false)} sx={{ alignSelf: 'center', mt: { xs: 0, sm: 1 } }}>
					<ChevronLeft />
				</IconButton>
			</Tooltip>
		)}
		<Box sx={{ display: isCollapsed ? 'none' : 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
			<PromptChat
					defaultTitle="Randol Content Assistant"
					showConversationNameBar
					adapter={adapter}
					emptyStateText="Ask me anything about this template."
					unavailableText="Save the template to start chatting."
				onComplete={onComplete}
				onAssistantMessageReceived={onAssistantMessageReceived}
				onDismiss={() => setIsCollapsed(true)}
				/>
			</Box>
		</Paper>
	);
}
